import { Component, Suspense, lazy } from 'react';
import { useAuthStore, useUiStore, useT } from '@/store';
import { useLiveSync } from '@/hooks/useLiveSync';
import { usePushNavigation } from '@/hooks/usePushNavigation';
import { Header } from '@/components/layout/Header';
import { AuthGate } from '@/components/auth/AuthGate';
import { ErrorState, SkeletonRows } from '@/components/ui/Panel';
import { CriticalToast } from '@/components/layout/NotificationBell';
import { BottomNav } from '@/components/layout/BottomNav';
import { DispatcherView } from '@/views/DispatcherView';
import { OrdersView } from '@/views/OrdersView';
import { FleetView } from '@/views/FleetView';
import { DataView } from '@/views/DataView';
import { DriversView } from '@/views/DriversView';

/**
 * Three views are code-split: each is opened occasionally rather than lived
 * in, and none of them is on the path a dispatcher takes on page load.
 * Analytics carries the chart machinery, Telegram the template editor, and
 * the client portal is a preview a dispatcher opens when a client calls.
 */
const AnalyticsView = lazy(() =>
  import('@/views/AnalyticsView').then((m) => ({ default: m.AnalyticsView })),
);
const TelegramView = lazy(() =>
  import('@/views/TelegramView').then((m) => ({ default: m.TelegramView })),
);
const ClientPortalView = lazy(() =>
  import('@/views/ClientPortalView').then((m) => ({ default: m.ClientPortalView })),
);
// Code-split like the three above. Note this defers the COMPONENT only: the
// pending-approval badge in the navigation runs the same query on every page
// load, so the data is already being fetched whether or not the tab is open.
const ApprovalsView = lazy(() =>
  import('@/views/ApprovalsView').then((m) => ({ default: m.ApprovalsView })),
);
const AlertsView = lazy(() =>
  import('@/views/AlertsView').then((m) => ({ default: m.AlertsView })),
);
// The fixed cockpit layout: heavier than any tab above it (its own map
// instance plus the analytics query), and like Analytics/Telegram/the
// client portal it is not where a session starts, so it pays its bundle
// cost only when opened.
const CommandCenterView = lazy(() =>
  import('@/views/CommandCenterView').then((m) => ({ default: m.CommandCenterView })),
);
// Code-split for the same reason: invoicing is a weekly or monthly sitting,
// not something a dispatcher lives in, and it is never the tab the console
// opens on.
const BillingView = lazy(() =>
  import('@/views/BillingView').then((m) => ({ default: m.BillingView })),
);
const LeavesView = lazy(() =>
  import('@/views/LeavesView').then((m) => ({ default: m.LeavesView })),
);
// Company details, tariffs, logins and the API key — read once a year at
// most, and never the tab the console opens on.
const AdminView = lazy(() =>
  import('@/views/AdminView').then((m) => ({ default: m.AdminView })),
);

const VIEWS = {
  dispatcher: DispatcherView,
  command: CommandCenterView,
  approvals: ApprovalsView,
  alerts: AlertsView,
  client: ClientPortalView,
  data: DataView,
  drivers: DriversView,
  orders: OrdersView,
  billing: BillingView,
  analytics: AnalyticsView,
  fleet: FleetView,
  telegram: TelegramView,
  leaves: LeavesView,
  admin: AdminView,
};

export default function App() {
  const apiKey = useAuthStore((s) => s.apiKey);
  const view = useUiStore((s) => s.view);

  // Subscribed once at the root. services/sse.js keeps a single shared
  // EventSource regardless of how many components subscribe, so this is
  // about where cache policy lives, not about connection count.
  useLiveSync();

  // Tapping a push notification focuses this tab and the service worker
  // sends the view it was about — otherwise the dispatcher lands on
  // whatever tab they left open, which is rarely the one the alert meant.
  usePushNavigation();

  if (!apiKey) return <AuthGate />;

  const ActiveView = VIEWS[view] ?? DispatcherView;

  return (
    <div className="min-h-full">
      <Header />
      {/* pb-24 on small screens clears the fixed BottomNav, which would
          otherwise sit on top of the last row of whatever view is open. */}
      <main className="mx-auto max-w-[1160px] p-3 pb-24 sm:p-[18px] md:pb-[18px]">
        <ViewErrorBoundary key={view}>
          <Suspense fallback={<SkeletonRows rows={8} />}>
            <ActiveView />
          </Suspense>
        </ViewErrorBoundary>
      </main>
      <CriticalToast />
      <BottomNav />
    </div>
  );
}

/**
 * Scoped to the view, keyed by view id: a crash in Analytics takes out
 * Analytics, and switching tabs resets it. In the monolith an exception in
 * any render function left the whole page half-drawn with no indication
 * that anything had failed.
 */
class ViewErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) return <ViewCrash error={this.state.error} onRetry={() => this.setState({ error: null })} />;
    return this.props.children;
  }
}

function ViewCrash({ error, onRetry }) {
  const t = useT();
  return (
    <div className="space-y-3">
      <ErrorState error={error} />
      <button type="button" className="btn" onClick={onRetry}>
        {t('common.loading2')}
      </button>
    </div>
  );
}
